import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { getLegacyRedirect } from "./lib/legacy-redirects";

// Permanent 301 redirects for URLs from the previous site. Runs before
// everything else so legacy paths never reach the router.
const legacyRedirectMiddleware = createMiddleware().server(
  async ({ next, request }) => {
    const url = new URL(request.url);
    const normalizedPath =
      url.pathname.length > 1 && url.pathname.endsWith("/")
        ? url.pathname.slice(0, -1)
        : url.pathname;

    const target = getLegacyRedirect(normalizedPath);
    if (!target) {
      // Permanent 301 from a trailing-slash URL to its canonical form
      // (e.g. /services/ to /services) for page requests only.
      const isPageRequest =
        (request.method === "GET" || request.method === "HEAD") &&
        !url.pathname.startsWith("/api/") &&
        !url.pathname.startsWith("/lovable/");
      if (isPageRequest && normalizedPath !== url.pathname) {
        return new Response(null, {
          status: 301,
          headers: { Location: normalizedPath + url.search },
        });
      }
      return next();
    }

    const search = url.search;
    const hashIndex = target.indexOf("#");
    const location =
      hashIndex === -1
        ? target + search
        : target.slice(0, hashIndex) + search + target.slice(hashIndex);

    return new Response(null, {
      status: 301,
      headers: { Location: location },
    });
  },
);



// Attaches the signed-in bearer token to server function calls. The backend
// client is imported lazily so its auth and realtime code stay out of the
// initial page bundle; it only loads when a server function is actually called.
const lazySupabaseAuth = createMiddleware({ type: "function" }).client(
  async ({ next }) => {
    const { supabase } = await import("@/integrations/supabase/client");
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    return next({
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },
);

const errorMiddleware = createMiddleware().server(async ({ next, request }) => {
  if (new URL(request.url).pathname.startsWith("/lovable/")) {
    return next();
  }
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

export const startInstance = createStart(() => ({
  functionMiddleware: [lazySupabaseAuth],
  requestMiddleware: [legacyRedirectMiddleware, errorMiddleware],
}));
